import { reactive } from 'vue'

import axios from 'axios'
import Hashes from 'jshashes'
import * as _ from 'lodash-es'
import moment from 'moment-timezone'

import alertService from '../../common/service/alertService.js'
import bookingStatus from '../../common/bookingStatus.js'
import calendarService from '../../leftCalendar/service/calendarService.js'
import colourService from './colourService.js'
import sequenceService from '../../common/service/sequenceService.js'
import jobPrefix from '../../common/service/jobPrefix.js'
import jobService from '../../job/service/jobService.js'
import jobStatus from '../../common/jobStatus.js'
import sliderService from '../../common/service/sliderService.js'

/*
 * Bookings.
 *
 * Was an Angular factory taking `ColourService`, the `BookingStatus` constant, `$http` and `$q`
 * (step 34 of #614). It imports those modules directly now and uses axios in place of `$http` -
 * app.js installs the Cognito token on the shared axios instance, which is the same job `$http`'s
 * interceptor did.
 */
function BookingService() {
  // The bookings map is seen by the resources calendar and the booking slide-out's conflict
  // warning; it is Vue.observable now (step 41 of #614) rather than watched through
  // `$rootScope.$watch`.
  const state = reactive({ bookingsForResources: {} })

  var bookingService = {
    createBooking: function (resource, startDate, endDate) {
      const currentJob = jobService.getCurrentJob()
      let jobNumber
      let initialStatus = bookingStatus.CONFIRMED
      if (currentJob) {
        jobNumber = currentJob.jobNumber
        initialStatus = currentJob.status === jobStatus.QUOTING ? bookingStatus.QUOTING : bookingStatus.CONFIRMED
      }

      var booking = {
        startsAt: new Date(startDate),
        endsAt: new Date(endDate),
        jobNumber: jobNumber,
        status: initialStatus,
      }

      return sequenceService
        .next('bookingEvent')
        .then(function (httpResult) {
          booking.bookingEvents = [
            {
              sequence: httpResult.data.nextItem,
              resource: resource,
              parentBookingSequence: booking.sequence,
            },
          ]
          return booking
        })
        .then(function (booking) {
          return sliderService
            .openSlider('BookingCtrl', 'booking.html', { booking: booking })
            .then(function (selectedItem) {
              if (jobService.getJobWindowOpen()) {
                bookingService.refreshBookings('day')
              }
              return booking
            })
        })
    },

    // `silent` suppresses the "Booking deleted" toast. A cut-and-paste deletes the original too,
    // but the user moved it rather than deleted it, and being told it was deleted on the way to
    // being told it was saved is just confusing.
    deleteBooking: function (booking, options) {
      const silent = Boolean(options && options.silent)

      if (!booking.jobNumber) {
        return axios.delete('/api/booking/' + booking._id).then(function () {
          // Refresh, or the event stays on the calendar: the delete went through, the alert said
          // it did, and the booking was still there to click (which then failed on the lookup).
          bookingService.refreshBookings('day')
          if (!silent) {
            alertService.add('success', 'Booking deleted')
          }
        })
      }

      return jobService.setJobByBooking(booking).then(function () {
        var parentJob = jobService.getCurrentJob()

        _.remove(parentJob.bookings, function (jobBooking) {
          return jobBooking.sequence === booking.sequence
        })

        if (parentJob._id) {
          jobService.save(booking)
        }

        // Always: the calendar is where the booking was deleted from, whether or not the job
        // window happens to be open.
        bookingService.refreshBookings('day')

        var msg = 'Booking for ' + jobPrefix.get() + parentJob.jobNumber + ' deleted'
        if (!silent) {
          alertService.add('success', msg)
        }
      })
    },

    deleteRepeatBooking: booking => {
      if (!booking.jobNumber) {
        return axios.delete('/api/repeatBooking/' + booking.repeatingId + '/' + booking.sequence).then(() => {
          alertService.add('success', 'Booking deleted')
        })
      }

      return jobService.setJobByBooking(booking).then(() => {
        var parentJob = jobService.getCurrentJob()

        _.remove(parentJob.bookings, jobBooking => {
          return (
            // remove me if:
            jobBooking.repeatingId === booking.repeatingId && // im in this repeating group
            jobBooking.sequence !== booking.sequence && // and not the booking i'm on
            jobBooking.status !== 'completed' && // and im not complete
            moment(jobBooking.startsAt).isSameOrAfter(moment(), 'day') // and my start date is today or later
          )
        })

        // remove the repeating group settings. completed and past bookings are ok to become non-repeaters
        for (const thisBooking of parentJob.bookings) {
          if (thisBooking.repeatingId === booking.repeatingId) {
            thisBooking.repeatEvery = undefined
            thisBooking.endsOn = undefined
            thisBooking.repeatingId = undefined
            thisBooking.includeWeekends = false
          }
        }

        if (parentJob._id) {
          jobService.save()
        }

        if (jobService.getJobWindowOpen()) {
          bookingService.refreshBookings('day')
        }

        var msg = 'Repeat booking for ' + jobPrefix.get() + parentJob.jobNumber + ' deleted'
        alertService.add('success', msg)

        return parentJob
      })
    },

    editBooking: function (booking) {
      jobService.setJobByBooking(booking).then(function () {
        let fullBooking
        if (booking.jobNumber) {
          fullBooking = _.filter(jobService.getCurrentJob().bookings, {
            sequence: booking.sequence,
          })[0]
        } else {
          fullBooking = booking
        }

        sliderService
          .openSlider('BookingCtrl', 'booking.html', {
            booking: _.cloneDeep(fullBooking),
          })
          .then(function (selectedItem) {
            if (jobService.getJobWindowOpen()) {
              bookingService.refreshBookings('day')
            }
          })
      })
    },

    getBookingsForResources: function () {
      return state.bookingsForResources
    },

    getRepeatBooking: async booking => {
      if (!booking.endsOn) {
        return Promise.resolve()
      }

      try {
        const httpResult = await axios.post('/api/repeating-dates', {
          booking: booking,
        })
        return httpResult.data
      } catch (error) {
        console.log(error)
      }
    },

    saveBooking: async (booking, suppressNotification, job) => {
      if (job) {
        jobService.setCurrentJob(job)
      } else {
        if (booking.sequence) {
          await jobService.setJobByBooking(booking)
        }
      }

      booking.startsAt = moment(booking.startsAt, 'DD-MM-YYYY HH:mm').toDate()
      booking.endsAt = moment(booking.endsAt, 'DD-MM-YYYY HH:mm').toDate()
      if (booking.endsOn) {
        booking.endsOn = moment(booking.endsOn, 'DD-MM-YYYY HH:mm').toDate()
      }
      booking.timezone = moment.tz.guess()

      if (!booking.sequence) {
        const sequenceName = booking.jobNumber ? 'booking' : 'adminBooking'
        const sequence = await sequenceService.next(sequenceName)
        booking.sequence = sequence.data.nextItem
      }

      await jobService.save({ bookingToSave: booking })
      if (!suppressNotification) {
        alertService.add('success', 'Booking saved!')
      }
    },

    refreshBookings: function (view) {
      var displayBookings = []

      var selectedDate = calendarService.getSelectedDate()

      return Promise.all([
        getJobBookings(displayBookings, selectedDate, view),
        getAdminBookings(displayBookings, selectedDate, view),
      ]).then(function () {
        // clear any existing bookings
        state.bookingsForResources = {}

        // Index bookings to display by their resource id
        for (const displayBooking of displayBookings) {
          if (displayBooking.resource) {
            var resourceId = displayBooking.resource._id
            var bookingsArray = state.bookingsForResources[resourceId] || []

            addBookingToArray(displayBooking, bookingsArray)
            state.bookingsForResources[resourceId] = bookingsArray
          }
        }

        return state.bookingsForResources
      })
    },

    gatherActivities: function () {
      return [
        'Music Composition',
        'Sound Design - Online',
        'Sound Design',
        'Copywriting',
        'Storyboard',
        'Graphic Design',
        'Producing',
        'Photography',
        'VFX Supervision',
        'Ingest/De-archive',
        'Offline Edit',
        'Motion Graphics/3D',
        'VFX',
        'Online/Grade',
        'Suite Dry Hire - Sound',
        'Suite Dry Hire - Vision',
        'Website',
        'Digital',
        'Other',
      ].sort()
    },

    gatherAdminBookingActivities: function () {
      return [
        'Leave – Personal',
        'Leave – Annual',
        'Rostered Time Off',
        'Training',
        'Meeting – Internal',
        'Meeting – Client/Supplier',
        'Quoting/Treatments',
        'Repairs/Maintenance',
        'Marketing/PR',
        'Out of Office',
        'Pending Job',
        'Other',
      ].sort()
    },

    findAdminBookingByBookingSequence: function (bookingSequence) {
      return axios.get('/api/booking/sequence/' + bookingSequence).then(function (httpResult) {
        return httpResult.data.bookings[0]
      })
    },

    findBookingByBookingEvent: async bookingEvent => {
      await jobService.setJobByBooking({
        sequence: bookingEvent.sequence,
      })

      if (!jobService.getCurrentJob()) {
        console.log('I expected there to be a job set from this bookingEvent but there was none:', bookingEvent)
        alertService.add('error', 'No job set when I needed one')
        throw Error('No job set when I needed one')
      } else {
        return jobService.getCurrentJob().bookings.find(booking => booking.sequence === bookingEvent.sequence)
      }
    },

    completeBooking: async booking => {
      const currentJob = await jobService.setJobByBooking({
        sequence: booking.sequence,
      })

      const currentBooking = currentJob.bookings.find(b => b.sequence === booking.sequence)

      if (currentBooking.status === bookingStatus.COMPLETED) {
        alertService.add('warning', 'A completed booking cannot be modified')
      }
    },

    getBookings: (selectedDate, view) => {
      let jobBookings = []

      return Promise.all([
        getJobBookings(jobBookings, selectedDate, view),
        getAdminBookings(jobBookings, selectedDate, view),
      ]).then(
        () => jobBookings,
        err => {
          console.log('Error getting bookings', err)
        }
      )
    },
  }

  function getJobBookings(jobBookingsToDisplay, selectedDate, view) {
    return jobService.getAllJobsForDate(selectedDate, view).then(function (httpResult) {
      // if editing a job, add those bookings (not saved to the server yet)
      var currentJob = jobService.getCurrentJob()
      if (currentJob && jobService.getJobWindowOpen()) {
        _.remove(httpResult.jobs, function (job) {
          return job.jobNumber === currentJob.jobNumber
        })
        httpResult.jobs.push(currentJob)
      }

      // for each job, put todays bookings events on the screen
      if (httpResult) {
        for (const job of httpResult.jobs) {
          job.bookings = job.bookings || []
          for (const booking of job.bookings) {
            for (const bookingEvent of booking.bookingEvents) {
              jobBookingsToDisplay.push(augmentBooking(booking, bookingEvent, job))
            }
          }
        }
      }
    })
  }

  function getAdminBookings(adminBookingsToDisplay, selectedDate, view) {
    return axios
      .get('/api/booking/date/' + view + '/' + moment(selectedDate).format('YYYY-MM-DD'))
      .then(function (httpResult) {
        // for each booking, put todays bookings events on the screen
        if (httpResult.data) {
          for (const booking of httpResult.data.bookings) {
            for (const bookingEvent of booking.bookingEvents) {
              adminBookingsToDisplay.push(augmentBooking(booking, bookingEvent))
            }
          }
        }
      })
  }

  function addBookingToArray(displayBooking, bookingsArray) {
    var index = _.findIndex(bookingsArray, ['sequence', displayBooking.sequence])

    if (index !== -1) {
      bookingsArray.splice(index, 1, displayBooking)
    } else {
      bookingsArray.push(displayBooking)
    }
  }

  function augmentBooking(booking, bookingEvent, job) {
    var actions = []
    actions.push({
      label: "<i class='glyphicon glyphicon-pencil'></i>",
      onClick: function (args) {
        bookingService.editBooking(booking)
      },
    })

    bookingEvent.actions = actions
    bookingEvent.startsAt = moment.tz(booking.startsAt, 'GMT').local().toDate()
    bookingEvent.endsAt = moment.tz(booking.endsAt, 'GMT').local().toDate()
    bookingEvent.draggable = true
    bookingEvent.resizable = true
    bookingEvent.status = booking.status
    bookingEvent.$id = bookingEvent.sequence
    bookingEvent.bookingNotes = booking.notes
    bookingEvent.parentBookingSequence = booking.sequence

    if (job) {
      bookingEvent.title = jobPrefix.get() + booking.jobNumber
      bookingEvent.description = job.title
      bookingEvent.jobDescription = job.description
      bookingEvent.color = colourService.getColour(booking)
    } else {
      bookingEvent.title = booking.calendarId ? 'External' : 'Admin'
      bookingEvent.description = booking.bookingTitle
        ? booking.bookingTitle + ' (' + booking.activity + ')'
        : booking.activity
      bookingEvent.jobDescription = ''
      booking.status = booking.calendarId ? bookingStatus.EXTERNAL : bookingStatus.ADMIN
      bookingEvent.color = colourService.getColour(booking)
    }

    // mwl-calendar watches the booking events with angular.equals
    // include the booking so that mwl-calendar will detect whether it has changed
    bookingEvent.bookingHash = new Hashes.MD5().hex(booking)

    return bookingEvent
  }

  return bookingService
}

// Constructed once, here - the injector built one and shared it.
export default BookingService()
